import produce from "immer";
import {
  RESET_CAPACITY_STARTED,
  RESET_CAPACITY_QUEUED,
  RESET_CAPACITY_ITEM_STARTED,
  RESET_CAPACITY_ITEM_COMPLETED,
  RESET_CAPACITY_ITEM_FAILED,
  RESET_CAPACITY_FINALIZING,
  RESET_CAPACITY_ALL_COMPLETED,
  RESET_CAPACITY_ALL_FAILED,
  RESET_CAPACITY_REQUEST_FAILED,
  RESET_CAPACITY_JOB_DISMISS,
} from "../types";

/**
 * Reset Capacity progress slice.
 * Shape: { jobs: { [jobId]: JobState } }
 * Mirrors the global multi-job pattern so the ProgressNotificationStack
 * selector renders N concurrent reset jobs without special-casing.
 */
const PROJECT_STATUS = {
  QUEUED: "QUEUED",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
};

const initialJobState = {
  active: false,
  open: false,
  startedAt: null,
  terminatedAt: null,
  totalProjects: 0,
  completedCount: 0,
  failedCount: 0,
  etaSeconds: null,
  avgSecondsPerWeight: null,
  error: null,
  projects: [],
};

export const initialState = { jobs: {} };

function upsertProject(job, index, patch) {
  if (index == null) return;
  const idx = job.projects.findIndex((p) => p.index === index);
  if (idx === -1) {
    job.projects.push({
      index,
      label: patch.label ?? "",
      status: PROJECT_STATUS.QUEUED,
      startedAt: null,
      completedAt: null,
      error: null,
      ...patch,
    });
    return;
  }
  Object.assign(job.projects[idx], patch);
}

export default function resetCapacityReducer(state = initialState, action) {
  const { type, payload } = action;
  switch (type) {
    case RESET_CAPACITY_STARTED:
      return produce(state, (draft) => {
        // Merge onto an existing job so a re-dispatched STARTED (rehydration)
        // updates the provided fields without zeroing the rest. Bespoke shape
        // (no ...payload), so apply each progress field only when present.
        const prev = draft.jobs[payload.jobId];
        const total = payload.total ?? payload.totalProjects;
        draft.jobs[payload.jobId] = {
          ...initialJobState,
          ...(prev ?? {}),
          active: true,
          open: true,
          startedAt: prev?.startedAt ?? payload.startedAt ?? 0,
          totalProjects: total ?? prev?.totalProjects ?? 0,
          completedCount: payload.completedCount ?? prev?.completedCount ?? 0,
          failedCount: payload.failedCount ?? prev?.failedCount ?? 0,
          etaSeconds: payload.etaSeconds ?? prev?.etaSeconds ?? null,
          jobId: payload.jobId,
        };
      });

    case RESET_CAPACITY_QUEUED:
      return produce(state, (draft) => {
        const job = draft.jobs[payload.jobId];
        if (!job) return;
        job.totalProjects = payload.total ?? 0;
        if (payload.etaSeconds != null) job.etaSeconds = payload.etaSeconds;
        if (payload.avgSecondsPerWeight != null) job.avgSecondsPerWeight = payload.avgSecondsPerWeight;
        job.projects = (payload.projects || []).map((p) => ({
          index: p.index,
          label: p.label,
          status: PROJECT_STATUS.QUEUED,
          startedAt: null,
          completedAt: null,
          error: null,
        }));
      });

    case RESET_CAPACITY_ITEM_STARTED:
      return produce(state, (draft) => {
        const job = draft.jobs[payload.jobId];
        if (!job) return;
        upsertProject(job, payload.index, {
          label: payload.label,
          status: PROJECT_STATUS.IN_PROGRESS,
          startedAt: payload.startedAt ?? 0,
        });
        if (payload.etaSeconds != null) job.etaSeconds = payload.etaSeconds;
      });

    case RESET_CAPACITY_ITEM_COMPLETED:
      return produce(state, (draft) => {
        const job = draft.jobs[payload.jobId];
        if (!job) return;
        upsertProject(job, payload.index, {
          label: payload.label,
          status: PROJECT_STATUS.COMPLETED,
          completedAt: payload.completedAt ?? 0,
          error: null,
        });
        job.completedCount = job.projects.filter((p) => p.status === PROJECT_STATUS.COMPLETED).length;
        job.failedCount = job.projects.filter((p) => p.status === PROJECT_STATUS.FAILED).length;
        if (payload.etaSeconds != null) job.etaSeconds = payload.etaSeconds;
      });

    case RESET_CAPACITY_ITEM_FAILED:
      return produce(state, (draft) => {
        const job = draft.jobs[payload.jobId];
        if (!job) return;
        upsertProject(job, payload.index, {
          label: payload.label,
          status: PROJECT_STATUS.FAILED,
          completedAt: payload.completedAt ?? 0,
          error: payload.error || "Unknown error",
        });
        job.completedCount = job.projects.filter((p) => p.status === PROJECT_STATUS.COMPLETED).length;
        job.failedCount = job.projects.filter((p) => p.status === PROJECT_STATUS.FAILED).length;
        if (payload.etaSeconds != null) job.etaSeconds = payload.etaSeconds;
      });

    case RESET_CAPACITY_FINALIZING:
      return produce(state, (draft) => {
        const job = draft.jobs[payload.jobId];
        if (!job) return;
        job.finalizing = true;
        job.finalizingLabel = payload.label || null;
        job.finalizingRows = payload.rows ?? null;
        if (payload.etaSeconds != null) job.etaSeconds = payload.etaSeconds;
      });

    case RESET_CAPACITY_ALL_COMPLETED:
      return produce(state, (draft) => {
        const job = draft.jobs[payload?.jobId];
        if (!job) return;
        job.finalizing = false;
        if (payload?.summary) job.resultSummary = payload.summary;
        if (payload?.rows != null) job.resultRows = payload.rows;
        job.active = false;
        job.etaSeconds = 0;
        job.terminatedAt = payload?.terminatedAt ?? Date.now();
      });

    case RESET_CAPACITY_ALL_FAILED:
    case RESET_CAPACITY_REQUEST_FAILED:
      return produce(state, (draft) => {
        const job = draft.jobs[payload?.jobId];
        if (!job) return;
        job.active = false;
        job.error = payload?.error || "Reset capacity failed";
        job.terminatedAt = payload?.terminatedAt ?? Date.now();
      });

    case RESET_CAPACITY_JOB_DISMISS:
      return produce(state, (draft) => {
        if (payload?.jobId === "all") {
          Object.keys(draft.jobs).forEach((id) => {
            if (!draft.jobs[id].active) delete draft.jobs[id];
          });
        } else if (payload?.jobId) {
          delete draft.jobs[payload.jobId];
        }
      });

    default:
      return state;
  }
}

export { PROJECT_STATUS };
